/**
 * Talking to the Google Website Translator widget.
 *
 * The widget has no public API. The two things it does expose are the
 * `googtrans` cookie (which it reads on load to decide the target language)
 * and the `.goog-te-combo` `<select>` it injects (which it listens to for
 * live switches). Everything here drives one or both of those.
 */

export type LangCode = "en" | "hi";

export const DEFAULT_LANG: LangCode = "en";

/** Passed to the widget as `includedLanguages`. */
export const GOOGLE_TRANSLATE_LANGS: LangCode[] = ["en", "hi"];

/**
 * Language names are written in their OWN script — the Hindi option reads
 * "हिन्दी" whichever language the page is currently showing. A reader who
 * can't read the current language still has to be able to find theirs, which
 * is why these are never themselves translated (and why the switcher carries
 * `notranslate`).
 */
export const LANGUAGES: { code: LangCode; label: string; short: string }[] = [
  { code: "en", label: "English", short: "EN" },
  { code: "hi", label: "हिन्दी", short: "हिं" },
];

export const isLangCode = (value: unknown): value is LangCode =>
  value === "en" || value === "hi";

const COOKIE = "googtrans";

/** The language the widget is currently set to, or null if it's untouched. */
export function readGoogTrans(): LangCode | null {
  if (typeof document === "undefined") return null;
  const raw = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${COOKIE}=`))
    ?.split("=")[1];
  if (!raw) return null;
  // Format is `/<source>/<target>`, e.g. `/en/hi`.
  const target = decodeURIComponent(raw).split("/")[2];
  return isLangCode(target) ? target : null;
}

/**
 * Write the cookie on every host spelling the widget might read it back from.
 *
 * Google's own widget sets it three ways — bare host, leading-dot domain, and
 * the registrable domain — and reads whichever it finds first. Writing only
 * one means the choice silently fails to survive a reload on some hosts
 * (`www.` vs apex being the usual culprit). `localhost` gets the bare form
 * only; browsers reject a `Domain=` attribute for it.
 */
function writeGoogTrans(value: string) {
  const host = window.location.hostname;
  const oneYear = 60 * 60 * 24 * 365;
  const base = `${COOKIE}=${value};path=/;max-age=${oneYear};SameSite=Lax`;

  document.cookie = base;
  if (host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return;

  document.cookie = `${base};domain=${host}`;
  document.cookie = `${base};domain=.${host}`;

  const registrable = host.split(".").slice(-2).join(".");
  if (registrable !== host) document.cookie = `${base};domain=.${registrable}`;
}

/** Expire the cookie everywhere it might have been written. */
function clearGoogTrans() {
  const host = window.location.hostname;
  const expired = `${COOKIE}=;path=/;max-age=0`;
  document.cookie = expired;
  if (host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return;
  document.cookie = `${expired};domain=${host}`;
  document.cookie = `${expired};domain=.${host}`;
  const registrable = host.split(".").slice(-2).join(".");
  if (registrable !== host) document.cookie = `${expired};domain=.${registrable}`;
}

/**
 * Switch the page language. Always reloads.
 *
 * The obvious implementation — set the widget's `<select>` and dispatch a
 * `change` — is what the widget documents, and it does NOT work here. Measured
 * on this site: setting the select on an already-rendered page left the
 * homepage entirely in English, while a fresh load with the same cookie
 * translated correctly. The widget walks the DOM it finds at load and its
 * mutation observer does not reliably pick up a tree React has already
 * committed, so the live path silently no-ops on exactly the pages that matter.
 *
 * Reverting to English has the same problem in reverse: the widget's own
 * "show original" leaves half-translated pages once React has re-rendered
 * underneath it.
 *
 * So both directions go through the cookie and a reload, which is the one path
 * that is reliable in both. The cost is a page load and a lost scroll position
 * on switch — a fair trade against a toggle that appears to do nothing.
 */
export function setGoogleTranslateLanguage(next: LangCode): void {
  if (next === DEFAULT_LANG) clearGoogTrans();
  else writeGoogTrans(`/${DEFAULT_LANG}/${next}`);

  window.location.reload();
}

/**
 * Re-run the translation over content the App Router swapped in.
 *
 * A client-side navigation replaces the page body without a document load, so
 * the widget never sees the new content and the destination renders in
 * English — measured going from a translated `/` to `/pricing`. Re-dispatching
 * `change` on the existing `<select>` is enough here, because this time the
 * widget already has its machinery mounted and only needs to be told to scan
 * again.
 */
export function retranslate(lang: LangCode): void {
  const select = document.querySelector<HTMLSelectElement>("select.goog-te-combo");
  if (!select) return;
  select.value = lang;
  select.dispatchEvent(new Event("change"));
}
